export interface SitemapRouteEntry {
  pageKey: string;
  changefreq: string;
  priority: string;
}

export interface RouteState {
  hasLanguagePrefix: boolean;
  languageCode: string;
  normalizedPath: string;
  pageKey: string;
  pagePath: string;
}

export interface AlternateLanguageLink {
  languageCode: string;
  hrefLang: string;
  url: string;
}

export const DEFAULT_SITE_HOST = "glt.md";
export const DEFAULT_SITE_ORIGIN = "https://glt.md";
export const DEFAULT_LANGUAGE_CODE = "en";
export const DEFAULT_OG_IMAGE_PATH = "/assets/img/en/glt_en.jpg";
export const DEFAULT_LOGO_PATH = "/assets/img/glt_logo_color.png";
export const SUPPORTED_LANGUAGE_CODES = ["en", "ro", "ru"];
export const LANGUAGE_LOCALE_MAP: Record<string, string> = {
  en: "en_US",
  ro: "ro_RO",
  ru: "ru_RU",
};
export const OG_IMAGE_PATH_BY_LANGUAGE: Record<string, string> = {
  en: "/assets/img/en/glt_en.jpg",
  ro: "/assets/img/ro/glt_ro.jpg",
  ru: "/assets/img/ru/glt_ru.jpg",
};
export const PAGE_PATH_BY_KEY: Record<string, string> = {
  index: "",
  about: "about",
  products: "products",
  contacts: "contacts",
};
const PAGE_KEY_BY_PATH: Record<string, string> = {
  "": "index",
  about: "about",
  products: "products",
  contacts: "contacts",
};

export const SITE_SITEMAP_ROUTES: SitemapRouteEntry[] = [
  {
    pageKey: "index",
    changefreq: "weekly",
    priority: "1.0",
  },
  {
    pageKey: "about",
    changefreq: "monthly",
    priority: "0.8",
  },
  {
    pageKey: "products",
    changefreq: "monthly",
    priority: "0.8",
  },
  {
    pageKey: "contacts",
    changefreq: "monthly",
    priority: "0.7",
  },
];

function trimSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, "");
}

function splitPath(pathname: string): string[] {
  const normalizedPathname = pathname.split(/[?#]/)[0];
  const trimmedPath = trimSlashes(normalizedPathname);

  return "" === trimmedPath ? [] : trimmedPath.split("/");
}

export function ensureLeadingSlash(path: string): string {
  let result = path;

  if (!result.startsWith("/")) {
    result = "/" + result;
  }

  return result;
}

export function buildAbsoluteUrl(origin: string, path: string): string {
  return origin + ensureLeadingSlash(path);
}

export function isSupportedLanguageCode(languageCode: string): boolean {
  return SUPPORTED_LANGUAGE_CODES.includes(languageCode.toLowerCase());
}

export function normalizeLanguageCode(languageCode: string, fallback = DEFAULT_LANGUAGE_CODE): string {
  let normalizedLanguageCode = languageCode.toLowerCase();
  let result = fallback.toLowerCase();

  if (isSupportedLanguageCode(normalizedLanguageCode)) {
    result = normalizedLanguageCode;
  }

  return result;
}

export function getNormalizedLanguageCodes(languageCodes: string[] = []): string[] {
  const result: string[] = [];

  languageCodes.forEach((languageCode) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode, "");

    if ("" !== normalizedLanguageCode && !result.includes(normalizedLanguageCode)) {
      result.push(normalizedLanguageCode);
    }
  });

  return 0 < result.length ? result : [...SUPPORTED_LANGUAGE_CODES];
}

export function getLanguageLocale(languageCode: string): string {
  const normalizedLanguageCode = normalizeLanguageCode(languageCode);

  return LANGUAGE_LOCALE_MAP[normalizedLanguageCode] || LANGUAGE_LOCALE_MAP[DEFAULT_LANGUAGE_CODE];
}

export function getOgImagePath(languageCode: string): string {
  const normalizedLanguageCode = normalizeLanguageCode(languageCode);

  return OG_IMAGE_PATH_BY_LANGUAGE[normalizedLanguageCode] || DEFAULT_OG_IMAGE_PATH;
}

export function parseRouteState(pathname: string, fallbackLanguageCode = DEFAULT_LANGUAGE_CODE): RouteState {
  const segments = splitPath(pathname);
  let hasLanguagePrefix = false;
  let languageCode = normalizeLanguageCode(fallbackLanguageCode);
  let pageSegments = segments;

  if (0 < segments.length && isSupportedLanguageCode(segments[0])) {
    hasLanguagePrefix = true;
    languageCode = normalizeLanguageCode(segments[0]);
    pageSegments = segments.slice(1);
  }

  const pagePath = pageSegments.join("/");
  const pageKey = PAGE_KEY_BY_PATH[pagePath] || "index";

  return {
    hasLanguagePrefix,
    languageCode,
    normalizedPath: ensureLeadingSlash(pagePath),
    pageKey,
    pagePath,
  };
}

export function buildLocalizedPagePath(pageKey: string, languageCode: string): string {
  const normalizedLanguageCode = normalizeLanguageCode(languageCode);
  const pagePath = PAGE_PATH_BY_KEY[pageKey] || "";
  let result = "/" + normalizedLanguageCode;

  if ("" !== pagePath) {
    result += "/" + pagePath;
  }

  return result;
}

export function buildLocalizedPath(pathname: string, languageCode: string): string {
  const pathnameMatch = pathname.match(/^([^?#]*)(.*)$/);
  const pathOnly = null !== pathnameMatch ? pathnameMatch[1] : pathname;
  const pathSuffix = null !== pathnameMatch ? pathnameMatch[2] : "";
  const routeState = parseRouteState(pathOnly, languageCode);

  return buildLocalizedPagePath(routeState.pageKey, languageCode) + pathSuffix;
}

export function buildAlternateLanguageLinks(origin: string, pageKey: string, languageCodes: string[] = []): AlternateLanguageLink[] {
  return getNormalizedLanguageCodes(languageCodes).map((languageCode) => ({
    languageCode,
    hrefLang: languageCode,
    url: buildAbsoluteUrl(origin, buildLocalizedPagePath(pageKey, languageCode)),
  }));
}

export function getRequestUrl(request: Request | null | undefined): URL | null {
  let result: URL | null = null;

  if (null !== request && undefined !== request) {
    try {
      result = new URL(request.url);
    } catch (_err) {
      result = null;
    }
  }

  return result;
}

export function getApiHostByHostName(hostName: string): string {
  let normalizedHostName = hostName.toLowerCase();
  let result = "api.glt.md";

  if ("" === normalizedHostName) {
    normalizedHostName = DEFAULT_SITE_HOST;
  }

  switch (normalizedHostName) {
    case "staging.glt.ro":
      result = "staging.api.glt.ro";
      break;
    case "glt.ro":
      result = "api.glt.ro";
      break;
    case "staging.glt-ltd.com":
      result = "staging.api.glt-ltd.com";
      break;
    case "glt-ltd.com":
      result = "api.glt-ltd.com";
      break;
    case "dev.glt.md":
      result = "dev.api.glt.md";
      break;
    case "staging.glt.md":
      result = "staging.api.glt.md";
      break;
    case "glt.md":
    default:
      result = "api.glt.md";
      break;
  }

  return result;
}
